/**
 * Rules shared by every surface that touches a chat attachment: what the
 * composer lets a user pick, what Content-Type the uploader stores, and how the
 * preview decides to render what came back.
 */

/**
 * A presigned single PUT can carry far more than this, but chat has no upload
 * progress UI and `sendMessage` fails a whole message if any one file errors,
 * so cap attachments at a size that stays responsive on a phone connection.
 */
export const CHAT_ATTACHMENT_MAX_BYTES = 100 * 1024 * 1024;

/**
 * Extensions accepted on top of any `image/*` file, mapped to the MIME type
 * browsers report for each.
 *
 * When changing this, update the `patientChat.attachmentTypeUnsupported` string
 * in every locale under `packages/i18n/messages` — it lists these formats to the
 * user and will otherwise drift.
 */
const MIME_TYPE_BY_EXTENSION: Record<string, string> = {
  ".pdf": "application/pdf",
  ".docx":
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".mov": "video/quicktime",
  ".mp4": "video/mp4",
  ".webm": "video/webm",
};

const ACCEPTED_EXTENSIONS = Object.keys(MIME_TYPE_BY_EXTENSION);
const ACCEPTED_MIME_TYPES = Object.values(MIME_TYPE_BY_EXTENSION);

/** Video formats, used to pick a thumbnail icon rather than an image preview. */
const VIDEO_EXTENSIONS = [".mov", ".mp4", ".webm"];

/**
 * `accept` only filters the picker's default view — a user can still choose
 * "All Files" — so anything that comes back is re-checked by
 * `validateChatAttachment`. Extensions and MIME types are both listed because
 * platforms differ in which one they supply for a given file.
 */
export const CHAT_ATTACHMENT_ACCEPT = [
  "image/*",
  ...ACCEPTED_EXTENSIONS,
  ...ACCEPTED_MIME_TYPES,
].join(",");

/**
 * This should only be used to get the filename from a filename, an S3 key or a
 * presigned S3 URL.
 */
export const getChatAttachmentFilename = (pathOrUrl: string): string => {
  const withoutQueryParams = pathOrUrl.split("?")[0];

  return decodeURIComponent(withoutQueryParams.split("/").pop() ?? "");
};

/**
 * Extension of an attachment, lower-cased and including the leading dot.
 * Lower-casing matters because camera rolls hand back names like `IMG_0001.MOV`.
 */
export const getChatAttachmentExtension = (pathOrUrl: string): string => {
  const filename = getChatAttachmentFilename(pathOrUrl);
  const lastDot = filename.lastIndexOf(".");

  return lastDot >= 0 ? filename.substring(lastDot).toLowerCase() : "";
};

export const isVideoAttachment = (pathOrUrl: string): boolean =>
  VIDEO_EXTENSIONS.includes(getChatAttachmentExtension(pathOrUrl));

export enum ChatAttachmentRejectionReason {
  TooLarge = "tooLarge",
  UnsupportedType = "unsupportedType",
}

/** The subset of `File` these rules need, so they stay testable outside a DOM. */
export interface ChatAttachmentCandidate {
  name: string;
  size: number;
  type: string;
}

export interface ChatAttachmentRejection {
  name: string;
  reason: ChatAttachmentRejectionReason;
}

export const isSupportedChatAttachment = ({
  name,
  type,
}: Omit<ChatAttachmentCandidate, "size">): boolean =>
  type.startsWith("image/") ||
  ACCEPTED_MIME_TYPES.includes(type) ||
  ACCEPTED_EXTENSIONS.includes(getChatAttachmentExtension(name));

/**
 * The Content-Type to store an attachment under.
 *
 * Prefers what the browser reported: the extension table lists only the formats
 * accepted *on top of* `image/*`, so it has no `.jpg` entry and a lookup-first
 * version stored every photo as `application/octet-stream`, which the viewer
 * offered as a download instead of rendering. Falls back to the table when a
 * platform reports an empty type, as some cloud-provider pickers do.
 */
export const resolveChatAttachmentContentType = (
  name: string,
  declaredType?: string
): string => {
  const declared = declaredType ?? "";

  // Not isSupportedChatAttachment(): that also accepts on extension, which
  // would validate an unsupported type like "application/x-msdownload"
  // paired with "clip.mp4".
  if (
    declared &&
    (declared.startsWith("image/") || ACCEPTED_MIME_TYPES.includes(declared))
  ) {
    return declared;
  }

  return (
    MIME_TYPE_BY_EXTENSION[getChatAttachmentExtension(name)] ??
    "application/octet-stream"
  );
};

/** Returns why the attachment can't be sent, or `null` when it's fine. */
export const validateChatAttachment = (
  candidate: ChatAttachmentCandidate
): ChatAttachmentRejection | null => {
  if (!isSupportedChatAttachment(candidate)) {
    return {
      name: candidate.name,
      reason: ChatAttachmentRejectionReason.UnsupportedType,
    };
  }

  if (candidate.size > CHAT_ATTACHMENT_MAX_BYTES) {
    return {
      name: candidate.name,
      reason: ChatAttachmentRejectionReason.TooLarge,
    };
  }

  return null;
};
